<template>
  <template v-if="user">
    <AppPaymentWallWrapper />
    <AppLoadingBar />
    <template v-if="showOnboardingWizard && isOnboardingRoute">
      <router-view />
    </template>

    <template v-else>
      <div :class="containerClasses">
        <AppSidebar
          v-if="showNewNavigationSidebar"
        />
        <AppTopMenu v-if="showTopMenu" />
        <main
          :class="wrapperClasses"
        >
          <p v-if="useNewNavigation">
            {{ navToggleText }}
            <button
              class="ui-link"
              @click="toggleNavigation"
            >
              {{ navToggleButtonText }}
            </button>
          </p>
          <AppMaxVolumeAlert />
          <AppTitleBar v-if="!isSettingsRoute" />
          <router-view />
        </main>
      </div>
      <NotificationPanel />
      <StatuspageWidget v-if="settings" />
      <AppModal />
    </template>
    <TheHijackFooter v-if="isHijacked" />
  </template>

  <AppErrorPage v-else />
</template>

<script setup lang="ts">
import { defineAsyncComponent, computed, onBeforeMount, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import Cookies from 'js-cookie'
import stringToBoolean from '@/common/utils/string-to-boolean'

import AppErrorPage from '@/application/components/AppErrorPage.vue'
import AppLoadingBar from '@/application/components/AppLoadingBar.vue'
import AppMaxVolumeAlert from '@/application/components/AppMaxVolumeAlert.vue'
import AppPaymentWallWrapper from '@/features/payment-wall/components/AppPaymentWallWrapper.vue'
import AppTitleBar from '@/application/components/AppTitleBar.vue'
import AppTopMenu from '@/application/components/old-navigation/AppTopMenu.vue'
import AppSidebar from '@/application/components/navigation/AppSidebar.vue'
import StatuspageWidget from '@/features/statuspage-widget/components/StatuspageWidget.vue'
import TheHijackFooter from '@/application/components/TheHijackFooter.vue'

import type { Settings, User } from '@/types/models'

import { SHIPPING_LABEL_DETAILS_ROUTE } from '@/features/label-details/routes'
import { RETURN_DETAILS_ROUTE } from '@/features/returns/routes'
import { SETTINGS_ROUTE } from '@/application/routes/settings.routes.names'
import { ONBOARDING_WIZARD_ROUTE } from '@/features/onboarding-wizard/routes'

const NotificationPanel = defineAsyncComponent(() => import('@/features/notifications/components/NotificationPanel.vue'))
const AppModal = defineAsyncComponent(() => import('@/application/components/AppModal.vue'))

import { NEW_NAVIGATION } from '@/application/beta-features'

const isNewNavigationEnabled = () => {
  return import.meta.env.VUE_APP_NEW_NAVIGATION_ENABLED
}

const store = useStore()
const route = useRoute()

const user = computed<User>(() => store.getters.user)
const settings = computed<Settings>(() => store.getters.settings)
const isHijacked = computed<boolean>(() => store.getters.isHijacked)
const showOnboardingWizard = computed<boolean>(() => store.getters.showOnboardingWizard)

const isUsingNewNav = ref<boolean>(false)

const userHasNewNavFeature = stringToBoolean(store.getters.hasBetaFeature(NEW_NAVIGATION))
const newNavEnvEnabled = stringToBoolean(isNewNavigationEnabled())

const useNewNavigation = userHasNewNavFeature || newNavEnvEnabled

onBeforeMount(() => {
  const preloader = document.querySelector('.preloader')
  if (preloader) {
    preloader.remove()
  }
  const cookieValue = Cookies.get('useNewNavigation')
  isUsingNewNav.value = cookieValue === 'true'
})

const toggleNavigation = () => {
  isUsingNewNav.value = !isUsingNewNav.value
  Cookies.set('useNewNavigation', isUsingNewNav.value.toString())
  window.location.reload()
}

const navToggleButtonText = computed<string>(() => {
  return isUsingNewNav.value ? 'Back' : 'Check Now!'
})

const navToggleText = computed<string>(() => {
  return isUsingNewNav.value ? 'Back to the previous navigation' : 'Try the new navigation'
})

const showNewNavigationSidebar = computed<boolean>(() => {
  return isUsingNewNav.value
})

const showTopMenu = computed<boolean>(() => {
  return !isUsingNewNav.value
})

const containerClasses = computed(() => ({
  'main-container': true,
  'layout-with-sidebar': showNewNavigationSidebar.value,
  'layout-with-top-navigation': !showNewNavigationSidebar.value && showTopMenu.value,
}))

const wrapperClasses = computed<string>(() => {
  if (route.name === SHIPPING_LABEL_DETAILS_ROUTE || route.name === RETURN_DETAILS_ROUTE) {
    return 'main-wrapper main-wrapper--narrow'
  } else {
    return 'main-wrapper'
  }
})

const isSettingsRoute = computed<boolean>(() => {
  return route.matched.some(route => route.name === SETTINGS_ROUTE)
})

const isOnboardingRoute = computed<boolean>(() => {
  return route.matched.some(route => route.name === ONBOARDING_WIZARD_ROUTE)
})

</script>
